import ajax from "plugins/AjaxService";
import { CHANNELS, Channel, CHANNEL_EVENT_NAMES } from "plugins/ChannelService";
import layoutSo from "stores/layout";
import { Cycle } from "types/Cycle";
import { Layer } from "types/Layer";
import { DateRange, DateString, Uuid } from "types/global";
import { dateRangeToDateRangeString } from 'utils';


// url builder
const urlFarmBuilder = (farmId: number, cycleId?: number): string => `farms/${farmId}/cycles${cycleId ? `/${cycleId}` : ""}`
const urlGrowUnitBuilder = (growUnitId: number, cycleId?: number): string => `grow_units/${growUnitId}/cycles${cycleId ? `/${cycleId}` : ""}`

/**
 * INDEX (FARM)
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-28095
 */
function indexByFarm(farmId: number, range?: DateRange): Promise<{ data: Cycle[] }> {
    const url = urlFarmBuilder(farmId)
    const r = dateRangeToDateRangeString(range)
    const query = `start_date=${r.start}&end_date=${r.end}`
    return ajax.get(`${url}?${query}`)
}

/**
 * INDEX (GROW UNIT)
 * https://airilabs.myjetbrains.com/youtrack/articles/ZOS-A-222/index
 */
function indexByGrowUnit(growUnitId: number, range?: DateRange) {
    const url = urlGrowUnitBuilder(growUnitId)
    const r = dateRangeToDateRangeString(range)
    const query = `start_date=${r.start}&end_date=${r.end}`
    return ajax.get(`${url}?${query}`)
}

/**
 * SHOW
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-28115
 */
function get(farmId: number, cycleId: number): Promise<{ data: Cycle }> {
    const url = urlFarmBuilder(farmId, cycleId)
    return ajax.get(url)
}

/**
 * https://airilabs.myjetbrains.com/youtrack/articles/ZOS-A-303/delay
 */
async function modifyByFarm(farmId: number, data: { cycles: Partial<Cycle>[] }): Promise<{ data: Cycle[] }> {
    const result = await ajax.post(`${urlFarmBuilder(farmId)}/delay`, data)
    return result
}



//#region DRAFT

/**
 * CREATE
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-28275
 */
async function defineDraft(cycle: DefineDataDraft, draftId: number, draftUuid: string): Promise<{ data: Cycle }> {
    return wrapChannelConfirm<{ data: Cycle }>(
        CHANNELS.CYCLETASKS,
        CHANNEL_EVENT_NAMES.TASK_DRAFTS_DEFINED,
        draftUuid,
        () => ajax.post(`cycles/cycles_drafts/${draftId}/define`, { cycle })
    )
}
export interface DefineDataDraft {
    growUnitId: number
    cropCycleId: number
    seedingDate: DateString
    phaseLayers: Partial<Layer>[]
}

/**
 * INDEX
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-28315
 */
async function indexDraft(draftId: number, range?: DateRange): Promise<{ data: Cycle[] }> {
    const url = `cycles/cycles_drafts/by_draft/${draftId}`
    const r = dateRangeToDateRangeString(range)
    const query = `start_date=${r.start}&end_date=${r.end}`
    return await ajax.get(`${url}?${query}`)
}

/**
 * BULK EDIT
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-29275
 */
async function bulkScheduleDraft(data: CycleDraftData, draftId: number, draftUuid: string): Promise<{ data: Cycle[] }> {
    if (!data) return
    if (data.rejected?.length == 0) {
        return ajax.post(`cycles/cycles_drafts/${draftId}/bulk_schedule`, data)
    }
    return wrapChannelConfirm<{ data: Cycle[] }>(
        CHANNELS.CYCLETASKS,
        CHANNEL_EVENT_NAMES.TASK_DRAFTS_CANCELLED,
        draftUuid,
        () => ajax.post(`cycles/cycles_drafts/${draftId}/bulk_schedule`, data)
    )
}
export type CycleDraftData = {
    scheduled?: Cycle[],
    rejected?: { refToCycleUuid: Uuid }[],
    undo?: { refToCycleUuid: Uuid }[],
}

//#endregion

/**
 * SPLIT
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-61595
 */
async function splitCycleDraft(
    cycle: Cycle, layersNumber: number[], draftId: number, draftUuid: string
): Promise<{ data: Cycle[] }> {

    if (!cycle || !layersNumber || !draftId) return
    const data = {
        cycle: {
            layers_to_split: layersNumber,
            cycle_uuid: cycle.cycleUuid,
            grow_unit_id: cycle.growUnitId,
            seeding_date: cycle.seedingDate,
        }
    }
    return wrapChannelConfirm<{ data:  Cycle[] }>(
        CHANNELS.CYCLETASKS,
        CHANNEL_EVENT_NAMES.TASK_DRAFTS_CANCELLED,
        draftUuid,
        () => ajax.post(`cycles/cycles_drafts/${draftId}/split`, data)
    )
}

//#region CROP LOT

/**
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-25595
 */
function harvestCropLot(lotCode: string, yieldAmount: number, unitsYield: number) {
    return ajax.post(
        `cycles/yield_by_lot_code`,
        { lotCode, yieldAmount, unitsYield }
    )
}

/**
 * EDIT USED SEED
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-59795
 */
export async function editCropLotSeedUsed(lotCode: string, seedUsedG: number): Promise<void> {
    return ajax.post(
        `cycles/used_seed_by_lot_code`,
        { lotCode, seedUsedG },
    )
}
/**
 * EDIT USED GEL
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-59995
 */
export async function editCropLotGelUsed(lotCode: string, gelUsedG: number): Promise<void> {
    return ajax.post(
        `cycles/used_gel_by_lot_code`,
        { lotCode, gelUsedG },
    )
}

//#endregion



async function wrapChannelConfirm<T>(topic: string, eventName: string, draftUuid: string, apiCall: () => Promise<T>): Promise<T> {
    const channel = new Channel(topic, { draft_uuid: draftUuid });
    layoutSo.setLoadingMsg("")

    const cleanupChannel = (handler: (message: any) => void, timeoutId: ReturnType<typeof setTimeout>) => {
        clearTimeout(timeoutId);
        channel.unsubscribe(handler);
    }
    let resolveCable: () => void
    const promiseCable = new Promise<void>((resolve) => resolveCable = resolve);
    const handleRequestChannel = (message: { eventName?: string }) => {
        if (message?.eventName === eventName) {
            cleanupChannel(handleRequestChannel, timeoutId);
            resolveCable();
        }
    }
    await channel.subscribe(handleRequestChannel)

    const timeoutId = setTimeout(() => {
        cleanupChannel(handleRequestChannel, timeoutId);
        resolveCable();
    }, 5000)

    const [apiCallResult] = await Promise.all([apiCall(), promiseCable])

    layoutSo.setLoadingMsg(null)
    return apiCallResult;
}

const api = {
    indexByFarm,
    indexByGrowUnit, // USATO IN LAYERS/TOWERS
    get,
    modifyByFarm, // DA ELIMINARE

    //#region DRAFT
    defineDraft,
    indexDraft,
    bulkScheduleDraft,
    splitCycleDraft,
    //#endregion

    //#region CROP LOT
    harvestCropLot,
    editCropLotSeedUsed,
    editCropLotGelUsed,
    //#endregion
}

export default api
